// All localized text for the SAA confirmation email, in one place.

export type Lang = 'nl' | 'en';

export type ProductId = 'aansprakelijkheid' | 'woonhuis' | 'inboedel' | 'personenauto' | 'rechtsbijstand' | 'ongevallen' | 'doorlopendereis';

// name = short label used in both the package section and the premium table. base = included/base
// coverage shown on its own line under the name (auto's variant is computed from the data instead).
export interface ProductNames {
  name: string;
  base?: string;
}

// Copy that differs when the request needs manual review (an acceptance question answered "Ja").
// Everything not listed here (labels, products, contact, premie, disclaimer) is shared with the
// confirmation copy.
export interface ReviewCopy {
  subject: string;
  title: string;
  intro: (pkg: string, brand: string) => string;
  ingangsdatum: string;
  vragenHeading: string;
  vragenIntro: string;
  toelichtingLabel: string;
  answerYes: string;
  akkoordHeading: string;
  akkoordLabel: string;
  watNuHeading: string;
  watNu: string[];
  afsluiting: (brand: string) => string;
}

export interface EmailCopy {
  products: Record<ProductId, ProductNames>;
  review: ReviewCopy;
  subject: string;
  title: string;
  greeting: (name: string) => string;
  intro: (pkg: string, brand: string) => string;
  pakketHeading: (pkg: string) => string;
  ingangsdatum: string;
  premieHeading: string;
  totaal: string;
  perMaand: string;
  betaalwijze: string;
  paymentMonthly: string;
  paymentYearly: string;
  feeSubscription: string;
  feeProlongation: string;
  gegevensHeading: string;
  labelNaam: string;
  labelAdres: string;
  labelGeboortedatum: string;
  labelEmail: string;
  labelTelefoon: string;
  gegevensFooter: string;
  watNuHeading: string;
  watNu: string[];
  contactHeading: string;
  contactIntro: string;
  contactTelefoon: string;
  contactEmail: string;
  contactOpeningstijden: string;
  contactOpeningstijdenValue: string;
  wistuHeading: string;
  wistu: string[];
  afsluiting: (brand: string) => string;
  groet: string;
  disclaimer: string;
}

export const EMAIL_COPY: Record<Lang, EmailCopy> = {
  nl: {
    products: {
      personenauto: { name: 'Auto' },
      woonhuis: { name: 'Woonhuis', base: 'Allrisk' },
      inboedel: { name: 'Inboedel', base: 'Allrisk' },
      aansprakelijkheid: { name: 'Aansprakelijkheid' },
      rechtsbijstand: { name: 'Rechtsbijstand', base: 'Consument & wonen' },
      ongevallen: { name: 'Ongevallen' },
      doorlopendereis: { name: 'Doorlopende reis' },
    },
    review: {
      subject: 'Uw aanvraag is ontvangen – in behandeling',
      title: 'Uw aanvraag wordt beoordeeld',
      intro: (pkg, brand) =>
        `Bedankt voor het samenstellen van uw ${pkg} via ${brand}. Omdat u één of meer acceptatievragen met "Ja" heeft beantwoord, wordt uw aanvraag eerst beoordeeld door onze acceptatieafdeling voordat wij u kunnen verzekeren.`,
      ingangsdatum: 'Gewenste ingangsdatum',
      vragenHeading: 'Uw antwoorden op de acceptatievragen',
      vragenIntro: 'U heeft onderstaande vraag/vragen met "Ja" beantwoord. Daarom beoordelen wij uw aanvraag handmatig.',
      toelichtingLabel: 'Toelichting',
      answerYes: 'Ja',
      akkoordHeading: 'Uw akkoordverklaringen',
      akkoordLabel: 'Akkoord',
      watNuHeading: 'Wat gebeurt er nu?',
      watNu: [
        'Onze acceptatieafdeling beoordeelt uw aanvraag. Dit kan enkele werkdagen in beslag nemen.',
        'Wij nemen zo spoedig mogelijk contact met u op over de uitkomst. Mogelijk vragen wij u om aanvullende informatie.',
        'Zolang uw aanvraag in behandeling is, bent u nog niet verzekerd. Uw dekking gaat pas in nadat uw aanvraag is goedgekeurd.',
      ],
      afsluiting: (brand) =>
        `Bedankt voor uw vertrouwen in ${brand}. Wij doen ons best om uw aanvraag zo snel mogelijk te beoordelen.`,
    },
    subject: 'Bevestiging van uw aanvraag',
    title: 'Bevestiging van uw',
    greeting: (name) => `Beste ${name},`,
    intro: (pkg, brand) =>
      `Bedankt voor het samenstellen van uw ${pkg} via ${brand}. We hebben uw aanvraag goed ontvangen en gaan hiermee voor u aan de slag.`,
    pakketHeading: (pkg) => `Uw ${pkg}`,
    ingangsdatum: 'Ingangsdatum',
    premieHeading: 'Uw maandelijkse premie',
    totaal: 'Totale premie',
    perMaand: 'p/m',
    betaalwijze: 'Betaalwijze',
    paymentMonthly: 'Maandelijks',
    paymentYearly: 'Jaarlijks',
    feeSubscription: 'Serviceabonnement',
    feeProlongation: 'Incassokosten',
    gegevensHeading: 'Uw gegevens',
    labelNaam: 'Naam',
    labelAdres: 'Adres',
    labelGeboortedatum: 'Geboortedatum',
    labelEmail: 'E-mail',
    labelTelefoon: 'Telefoonnummer',
    gegevensFooter: 'Klopt er iets niet? Laat het ons weten, dan passen we dit direct voor u aan.',
    watNuHeading: 'Wat kunt u van ons verwachten?',
    watNu: [
      'We controleren en verwerken uw aanvraag binnen 3 – 5 werkdagen.',
      'U ontvangt de polis(sen) per e-mail.',
      'Uw dekking gaat in op de gekozen ingangsdatum.',
    ],
    contactHeading: 'Vragen? We helpen u graag',
    contactIntro: 'Heeft u vragen over uw verzekeringen of wilt u iets aanpassen? Neem gerust contact met ons op.',
    contactTelefoon: 'Telefoon',
    contactEmail: 'E-mail',
    contactOpeningstijden: 'Openingstijden',
    contactOpeningstijdenValue: '24/7 bereikbaar',
    wistuHeading: 'Wist u dat...',
    wistu: [
      'u uw pakket later altijd kunt aanpassen.',
      'wij u helpen bij schade of wijzigingen.',
      'u ook terecht kunt voor hypotheekadvies bij DFD.',
    ],
    afsluiting: (brand) =>
      `Bedankt voor uw vertrouwen in ${brand}. Wij zorgen ervoor dat u goed verzekerd bent, zodat u kunt focussen op wat écht belangrijk is.`,
    groet: 'Met vriendelijke groet,',
    disclaimer:
      'Deze e-mail is een bevestiging van uw aanvraag en vormt geen polis of definitieve acceptatie. Aan de genoemde premies, dekkingen en datums kunnen geen rechten worden ontleend tot uw aanvraag is verwerkt en de polis(sen) zijn afgegeven. Op onze dienstverlening zijn algemene voorwaarden van toepassing. Wilt u geen e-mails meer ontvangen of uw gegevens inzien? Neem dan contact met ons op.',
  },
  en: {
    products: {
      personenauto: { name: 'Car' },
      woonhuis: { name: 'Home', base: 'All-risk' },
      inboedel: { name: 'Contents', base: 'All-risk' },
      aansprakelijkheid: { name: 'Liability' },
      rechtsbijstand: { name: 'Legal aid', base: 'Consumer & living' },
      ongevallen: { name: 'Accident' },
      doorlopendereis: { name: 'Travel' },
    },
    review: {
      subject: 'Your request has been received – under review',
      title: 'Your request is being reviewed',
      intro: (pkg, brand) =>
        `Thank you for putting together your ${pkg} via ${brand}. Because you answered one or more acceptance questions with "Yes", our acceptance department will first review your request before we can insure you.`,
      ingangsdatum: 'Preferred start date',
      vragenHeading: 'Your answers to the acceptance questions',
      vragenIntro: 'You answered the question(s) below with "Yes". We therefore review your request manually.',
      toelichtingLabel: 'Explanation',
      answerYes: 'Yes',
      akkoordHeading: 'Your declarations',
      akkoordLabel: 'Agreed',
      watNuHeading: 'What happens next?',
      watNu: [
        'Our acceptance department will review your request. This may take a few business days.',
        'We will contact you about the outcome as soon as possible. We may ask you for additional information.',
        'While your request is under review, you are not yet insured. Your coverage only starts once your request has been approved.',
      ],
      afsluiting: (brand) =>
        `Thank you for your trust in ${brand}. We will do our best to review your request as quickly as possible.`,
    },
    subject: 'Confirmation of your request',
    title: 'Confirmation of your',
    greeting: (name) => `Dear ${name},`,
    intro: (pkg, brand) =>
      `Thank you for putting together your ${pkg} via ${brand}. We have received your request and are getting to work on it.`,
    pakketHeading: (pkg) => `Your ${pkg}`,
    ingangsdatum: 'Start date',
    premieHeading: 'Your monthly premium',
    totaal: 'Total premium',
    perMaand: 'p/m',
    betaalwijze: 'Payment method',
    paymentMonthly: 'Monthly',
    paymentYearly: 'Yearly',
    feeSubscription: 'Service subscription',
    feeProlongation: 'Collection fee',
    gegevensHeading: 'Your details',
    labelNaam: 'Name',
    labelAdres: 'Address',
    labelGeboortedatum: 'Date of birth',
    labelEmail: 'E-mail',
    labelTelefoon: 'Phone number',
    gegevensFooter: 'Is something not right? Let us know and we will adjust it for you right away.',
    watNuHeading: 'What can you expect from us?',
    watNu: [
      'We review and process your request within 3 – 5 business days.',
      'You will receive your policy/policies by e-mail.',
      'Your coverage starts on the chosen start date.',
    ],
    contactHeading: 'Questions? We are happy to help',
    contactIntro: 'Do you have questions about your insurance or would you like to change something? Please get in touch.',
    contactTelefoon: 'Phone',
    contactEmail: 'E-mail',
    contactOpeningstijden: 'Opening hours',
    contactOpeningstijdenValue: 'Available 24/7',
    wistuHeading: 'Did you know...',
    wistu: [
      'you can always adjust your package later.',
      'we help you with claims or changes.',
      'you can also turn to DFD for mortgage advice.',
    ],
    afsluiting: (brand) =>
      `Thank you for your trust in ${brand}. We make sure you are well insured, so you can focus on what truly matters.`,
    groet: 'Kind regards,',
    disclaimer:
      'This e-mail is a confirmation of your request and does not constitute a policy or final acceptance. No rights can be derived from the premiums, coverages and dates mentioned until your request has been processed and the policy/policies have been issued. Our general terms and conditions apply to our services. If you no longer wish to receive e-mails or want to view your data, please contact us.',
  },
};

// NU'91 addresses the customer as je/jij; DFD and SPD keep the formal copy above.
const NL_INFORMAL: EmailCopy = {
  ...EMAIL_COPY.nl,
  review: {
    ...EMAIL_COPY.nl.review,
    subject: 'Je aanvraag is ontvangen – in behandeling',
    title: 'Je aanvraag wordt beoordeeld',
    intro: (pkg, brand) =>
      `Bedankt voor het samenstellen van je ${pkg} via ${brand}. Omdat je één of meer acceptatievragen met "Ja" hebt beantwoord, wordt je aanvraag eerst beoordeeld door onze acceptatieafdeling voordat wij je kunnen verzekeren.`,
    vragenHeading: 'Je antwoorden op de acceptatievragen',
    vragenIntro: 'Je hebt onderstaande vraag/vragen met "Ja" beantwoord. Daarom beoordelen wij je aanvraag handmatig.',
    akkoordHeading: 'Je akkoordverklaringen',
    watNu: [
      'Onze acceptatieafdeling beoordeelt je aanvraag. Dit kan enkele werkdagen in beslag nemen.',
      'Wij nemen zo snel mogelijk contact met je op over de uitkomst. Mogelijk vragen wij je om aanvullende informatie.',
      'Zolang je aanvraag in behandeling is, ben je nog niet verzekerd. Je dekking gaat pas in nadat je aanvraag is goedgekeurd.',
    ],
    afsluiting: (brand) =>
      `Bedankt voor je vertrouwen in ${brand}. Wij doen ons best om je aanvraag zo snel mogelijk te beoordelen.`,
  },
  subject: 'Bevestiging van je aanvraag',
  title: 'Bevestiging van je',
  intro: (pkg, brand) =>
    `Bedankt voor het samenstellen van je ${pkg} via ${brand}. We hebben je aanvraag goed ontvangen en gaan hiermee voor je aan de slag.`,
  pakketHeading: (pkg) => `Jouw ${pkg}`,
  premieHeading: 'Je maandelijkse premie',
  gegevensHeading: 'Jouw gegevens',
  gegevensFooter: 'Klopt er iets niet? Laat het ons weten, dan passen we dit direct voor je aan.',
  watNuHeading: 'Wat kun je van ons verwachten?',
  watNu: [
    'We controleren en verwerken je aanvraag binnen 3 – 5 werkdagen.',
    'Je ontvangt de polis(sen) per e-mail.',
    'Je dekking gaat in op de gekozen ingangsdatum.',
  ],
  contactHeading: 'Vragen? We helpen je graag',
  contactIntro: 'Heb je vragen over je verzekeringen of wil je iets aanpassen? Neem gerust contact met ons op.',
  wistuHeading: 'Wist je dat...',
  wistu: [
    'je je pakket later altijd kunt aanpassen.',
    'wij je helpen bij schade of wijzigingen.',
    'je ook terecht kunt voor hypotheekadvies bij DFD.',
  ],
  afsluiting: (brand) =>
    `Bedankt voor je vertrouwen in ${brand}. Wij zorgen ervoor dat je goed verzekerd bent, zodat je kunt focussen op wat écht belangrijk is.`,
  disclaimer:
    'Deze e-mail is een bevestiging van je aanvraag en vormt geen polis of definitieve acceptatie. Aan de genoemde premies, dekkingen en datums kunnen geen rechten worden ontleend tot je aanvraag is verwerkt en de polis(sen) zijn afgegeven. Op onze dienstverlening zijn algemene voorwaarden van toepassing. Wil je geen e-mails meer ontvangen of je gegevens inzien? Neem dan contact met ons op.',
};

/** Copy for a language and tone: informal Dutch for NU'91, the formal base for DFD/SPD; English has no split. */
export const getEmailCopy = (lang: Lang, informal: boolean): EmailCopy =>
  lang === 'nl' && informal ? NL_INFORMAL : EMAIL_COPY[lang];
